import { useEffect, useRef, useCallback, useState } from 'react';
import { tokenStore } from '../services/api';

const WS_BASE = process.env.REACT_APP_WS_URL || 'ws://localhost:4000/ws';

// SEC-27 / I10 — reconnect backoff: 1 s doubling up to 30 s, with ±25% jitter
// so a fleet of tabs does not stampede the server after a restart.
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
// Server closes with this code when the first-message auth is rejected. The
// token is invalid/expired — reconnecting would only loop; the HTTP 401 flow
// (refresh → re-login) is what fixes it.
const WS_CLOSE_AUTH_FAILED = 4001;

function nextReconnectDelay(attempt) {
  const exp = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
  const jitter = exp * 0.25 * (Math.random() * 2 - 1);
  return Math.max(RECONNECT_BASE_MS / 2, Math.round(exp + jitter));
}

/**
 * Custom hook for WebSocket connection with document presence and real-time
 * collaboration support (activity feed, typing indicators, version events).
 *
 * SEC-27 / M1 — the access token never travels in the URL (it would land in
 * proxy/access logs). The socket opens bare and the first frame is
 * `{ event: 'auth', data: { token } }`; nothing else is sent until the server
 * answers `{ event: 'auth.ok', data: { userId, userName } }`.
 */
export function useWebSocket({
  documentId,
  userName: propUserName,
  onPresenceUpdate,
  onVersionCreated,
  onWorkflowInvalidated,
  onUserActivity,
}) {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const heartbeatIntervalRef = useRef(null);
  // Frames queued while waiting for auth.ok — flushed once authenticated.
  const outboxRef = useRef([]);
  const authedRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  // activityFeed: array of { userId, userName, activityType, description, timestamp }
  const [activityFeed, setActivityFeed] = useState([]);
  // typingUsers: Set of userIds currently typing/editing
  const [typingUsers, setTypingUsers] = useState(new Map()); // userId -> { userName, timer }
  const typingTimersRef = useRef(new Map());

  /**
   * Mark a user as typing and auto-clear after 4 seconds of inactivity.
   */
  const markUserTyping = useCallback((userId, userName) => {
    // Clear existing timer for this user
    if (typingTimersRef.current.has(userId)) {
      clearTimeout(typingTimersRef.current.get(userId));
    }

    setTypingUsers(prev => {
      const next = new Map(prev);
      next.set(userId, userName);
      return next;
    });

    // Auto-remove after 4 seconds
    const timer = setTimeout(() => {
      setTypingUsers(prev => {
        const next = new Map(prev);
        next.delete(userId);
        return next;
      });
      typingTimersRef.current.delete(userId);
    }, 4000);

    typingTimersRef.current.set(userId, timer);
  }, []);

  // Send a frame now if authenticated, otherwise park it until auth.ok.
  const sendOrQueue = useCallback((ws, frame) => {
    if (authedRef.current && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(frame));
    } else {
      outboxRef.current.push(frame);
    }
  }, []);

  const connect = useCallback(() => {
    // Use the tenant-aware token store (session for impersonation tabs, local
    // otherwise). Reading localStorage directly would pick up the owner's
    // token in impersonation tabs, registering WS presence as the wrong user.
    const token = tokenStore().getItem('accessToken');
    if (!token) return;

    // SEC-27: bare URL — no token, tenant or name in the query string.
    // L5: the display name is whatever the server puts in auth.ok (it knows
    // the user from the token); the JWT is no longer decoded client-side.
    try {
      const ws = new WebSocket(WS_BASE);
      wsRef.current = ws;
      authedRef.current = false;
      outboxRef.current = [];

      ws.onopen = () => {
        // First frame: authenticate. Everything else waits for auth.ok.
        ws.send(JSON.stringify({ event: 'auth', data: { token } }));

        // Join document room if documentId is provided (queued until auth.ok)
        if (documentId) {
          sendOrQueue(ws, {
            event: 'document.presence.join',
            data: { documentId },
          });
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const { event: eventType, data } = message;

          switch (eventType) {
            case 'auth.ok': {
              authedRef.current = true;
              reconnectAttemptRef.current = 0; // I10: backoff resets on success
              setConnected(true);

              // Flush whatever was queued while we waited for the handshake.
              const pending = outboxRef.current;
              outboxRef.current = [];
              for (const frame of pending) {
                if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(frame));
              }

              // Start heartbeat
              clearInterval(heartbeatIntervalRef.current);
              heartbeatIntervalRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN && documentId) {
                  ws.send(JSON.stringify({
                    event: 'document.presence.heartbeat',
                    data: { documentId },
                  }));
                }
              }, 30000); // 30 seconds
              break;
            }

            case 'document.presence.update':
              setActiveUsers(data.users || []);
              if (onPresenceUpdate) onPresenceUpdate(data.users || []);
              break;

            case 'document.version.created': {
              if (onVersionCreated) onVersionCreated(data);

              // Push a clickable "version saved" entry into the activity feed
              // so co-editors can click it to jump straight to the diff view.
              if (data.versionNumber > 1) {
                const versionEntry = {
                  id: `version-${data.versionNumber}-${data.timestamp}`,
                  userId: data.savedBy || 'system',
                  userName: data.savedBy || 'Someone',
                  activityType: 'version_saved',
                  description: `saved version v${data.versionNumber}`,
                  timestamp: data.timestamp,
                  // Navigation data for "View Changes" link
                  diffUrl: `/contracts/${data.contractId}/documents/${data.documentId}/compare?v1=${data.versionNumber - 1}&v2=${data.versionNumber}`,
                  versionNumber: data.versionNumber,
                };
                setActivityFeed(prev => [versionEntry, ...prev].slice(0, 50));
                if (onUserActivity) onUserActivity(versionEntry);
              }
              break;
            }

            case 'workflow.invalidated':
              if (onWorkflowInvalidated) onWorkflowInvalidated(data);
              break;

            case 'document.user.activity': {
              // Update typing indicator
              if (data.activityType === 'editing' || data.activityType === 'typing') {
                markUserTyping(data.userId, data.userName);
              }

              // Append to activity feed (keep last 50 entries)
              const entry = {
                id: `${data.userId}-${data.timestamp}`,
                userId: data.userId,
                userName: data.userName,
                activityType: data.activityType,
                description: data.description,
                timestamp: data.timestamp,
              };
              setActivityFeed(prev => [entry, ...prev].slice(0, 50));

              if (onUserActivity) onUserActivity(entry);
              break;
            }

            case 'connected':
              // Connection acknowledged
              break;

            default:
              break;
          }
        } catch (err) {
          console.error('[WS] Message parse error:', err);
        }
      };

      ws.onclose = (event) => {
        setConnected(false);
        authedRef.current = false;
        outboxRef.current = [];
        clearInterval(heartbeatIntervalRef.current);

        // 1000 = we closed it on purpose. 4001 = server rejected the token;
        // reconnecting with the same token would just loop (SEC-27).
        if (event.code === 1000 || event.code === WS_CLOSE_AUTH_FAILED) return;

        // I10: exponential backoff with jitter instead of a fixed 3 s.
        const delay = nextReconnectDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };

      ws.onerror = () => {
        // onclose will handle reconnection
      };
    } catch (err) {
      console.error('[WS] Connection error:', err);
    }
  // eslint-disable-next-line
  }, [documentId, onPresenceUpdate, onVersionCreated, onWorkflowInvalidated, onUserActivity, markUserTyping, sendOrQueue]);

  // Send a message through the WebSocket (queued until the auth handshake
  // completes; dropped if there is no socket at all).
  const send = useCallback((event, data) => {
    const ws = wsRef.current;
    if (!ws) return;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      sendOrQueue(ws, { event, data });
    }
  }, [sendOrQueue]);

  /**
   * Broadcast that the current user is actively editing.
   * Call this from ONLYOFFICE event handlers or on user interaction.
   */
  const broadcastActivity = useCallback((activityType = 'editing', description = null) => {
    if (!documentId) return;
    send('document.user.activity', {
      documentId,
      activityType,
      description,
    });
  }, [documentId, send]);

  // Leave document and close connection
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      if (documentId && authedRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          event: 'document.presence.leave',
          data: { documentId },
        }));
      }
      wsRef.current.close(1000, 'User left');
    }
    authedRef.current = false;
    outboxRef.current = [];
    reconnectAttemptRef.current = 0;
    clearInterval(heartbeatIntervalRef.current);
    clearTimeout(reconnectTimeoutRef.current);
    // Clear all typing timers
    for (const timer of typingTimersRef.current.values()) {
      clearTimeout(timer);
    }
    typingTimersRef.current.clear();
  }, [documentId]);

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  // Reconnect when documentId changes. The display name no longer rides on
  // the connection (the server resolves it from the token in auth.ok), so a
  // late Redux user hydration does not need a fresh socket.
  // eslint-disable-next-line
  }, [documentId]);

  return {
    connected,
    activeUsers,
    activityFeed,
    typingUsers,   // Map<userId, userName> of users currently typing
    send,
    broadcastActivity,
    disconnect,
  };
}

export default useWebSocket;
